import { createContext, useContext, useMemo } from 'react';
import { Path } from '@src/navigations/routes';
import { useAccount } from '@src/contexts';
import { useLocation } from 'react-router-dom';
import { useGetBusinessInformationCompletionCheck } from '@src/hooks/queries/onboarding';
import useOnboardingVerificationCheck from '@src/pages/onboarding/hooks/useOnboardingVerificationCheck';
import useRaasOnboardingVerificationCheck from '@src/pages/onboardingRemittance/hooks/useOnboardingVerificationCheck';
import useDebounce from '@src/hooks/utils/useDebounce';

interface BusinessCheckContextType {
  resolvedPath: string | undefined;
}

const BusinessCheckContext = createContext<BusinessCheckContextType | undefined>(undefined);

export const BusinessCheckProvider = ({ children }: { children: React.ReactNode }) => {
  const { isLoggedIn, businessVariant, user, isRemittanceBusiness } = useAccount();
  const { pathname } = useLocation();

  const { data: businessCheckData, isLoading: businessCheckLoading } =
    useGetBusinessInformationCompletionCheck({
      enabled: isLoggedIn && user?.userOnboardingStatus === 2
    });

  // Check if all business requirements are met
  const isBusinessComplete =
    businessCheckData &&
    businessCheckData?.isBusinessInfomationCompleted &&
    businessCheckData?.isBusinessDocumentCompleted &&
    businessCheckData?.isControlPersonCompleted &&
    businessCheckData?.isSubmitted;

  const {
    businessInformationDetailsLoading: raasBusinessInformationDetailsLoading,
    isOnboardingValidated: isRaasOnboardingValidated
  } = useRaasOnboardingVerificationCheck({
    isBusinessComplete: !!isBusinessComplete && isRemittanceBusiness
  });

  const {
    businessInformationDetailsLoading: businessInformationDetailsLoading,
    isOnboardingValidated: isOnboardingValidated
  } = useOnboardingVerificationCheck({
    isBusinessComplete: !!isBusinessComplete && !isRemittanceBusiness
  });

  const isLoading =
    businessCheckLoading ||
    raasBusinessInformationDetailsLoading ||
    businessInformationDetailsLoading;

  const debouncedIsLoading = useDebounce(isLoading);

  const isOnboardingPage = isRemittanceBusiness
    ? pathname === Path.OnboardingRemittance
    : pathname === Path.Onboarding;

  const remittancePages = [Path.SenderSetupGuide, Path.SenderApiKeysWebhooks];

  const isReceiverPages = pathname.startsWith('/receiver');
  const isSenderPages =
    pathname.startsWith('/sender') &&
    !remittancePages.filter((item) => pathname.includes(item)).length;
  const isRemittancePages = pathname.startsWith('/sender');

  const resolvedPath = useMemo(() => {
    /**
     * 1 = user hasn't filled his profile 2 = user has filled his profile
     */
    const userOnboardingStatus = user?.userOnboardingStatus;

    // if (!isLoggedIn) {
    //   return Path.Login;
    // }

    if (debouncedIsLoading) return 'loading';

    if (isLoggedIn && !debouncedIsLoading) {
      // If user has completed onboarding (status 2) AND all business checks are complete
      if (userOnboardingStatus === 2 && isBusinessComplete) {
        // Then check business type and redirect accordingly
        if (businessVariant === 'receiver') {
          if (!isOnboardingValidated) {
            if (!isOnboardingPage) {
              return Path.Onboarding;
            }
          } else if (!isReceiverPages) {
            return Path.ReceiverDashboard;
          }
        }
        if (isRemittanceBusiness) {
          if (!isRaasOnboardingValidated) {
            if (!isOnboardingPage) {
              return Path.OnboardingRemittance;
            }
          } else if (!isRemittancePages) {
            return Path.SenderSetupGuide;
          }
        } else {
          if (businessVariant === 'sender') {
            if (!isOnboardingValidated) {
              if (!isOnboardingPage) {
                return Path.Onboarding;
              }
            } else if (!isSenderPages) {
              return Path.SenderDashboard;
            }
          }
        }
      }

      // If either onboarding is not complete OR business checks are not complete
      if ((userOnboardingStatus === 1 || !isBusinessComplete) && !isOnboardingPage) {
        if (isRemittanceBusiness) {
          return Path.OnboardingRemittance;
        }
        return Path.Onboarding;
      }
    }
  }, [
    user,
    businessCheckLoading,
    raasBusinessInformationDetailsLoading,
    businessInformationDetailsLoading,
    isLoggedIn,
    isBusinessComplete,
    isOnboardingValidated,
    isLoggedIn,
    isBusinessComplete,
    isOnboardingValidated,
    isOnboardingPage,
    businessVariant,
    isReceiverPages,
    isRemittanceBusiness,
    isRaasOnboardingValidated,
    isRemittancePages,
    isSenderPages,
    isRaasOnboardingValidated,
    isRemittancePages,
    isSenderPages,
    debouncedIsLoading
  ]);

  const value: BusinessCheckContextType = { resolvedPath };

  return <BusinessCheckContext.Provider value={value}>{children}</BusinessCheckContext.Provider>;
};

export const useBusinessCheckContext = () => {
  const context = useContext(BusinessCheckContext);
  if (!context) {
    throw new Error('useBusinessCheckContext must be used within a BusinessCheckProvider');
  }
  return context;
};
